import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Calendar, Users, FileText, LayoutDashboard, Settings, MessageCircle, History, Dumbbell, BookOpen, BarChart3 } from "lucide-react";
import CalendarView from "@/components/CalendarView";
import ClientsPage from "@/components/ClientsPage";
import TrainerPlansPage from "@/pages/TrainerPlansPage";
import SettingsPage from "@/pages/SettingsPage";
import MessagesAdminPage from "@/pages/MessagesAdminPage";
import LessonsHistoryPage from "@/pages/LessonsHistoryPage";
import LoadHistoryPage from "@/pages/LoadHistoryPage";
import RegulationPage from "@/pages/RegulationPage";
import StatsPage from "@/pages/StatsPage";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

const nav = [
  { key: "dash", to: "/trainer", label: "Overview", icon: <LayoutDashboard size={16} />, end: true },
  { key: "cal", to: "/trainer/calendar", label: "Calendario", icon: <Calendar size={16} /> },
  { key: "clients", to: "/trainer/clients", label: "Clienti", icon: <Users size={16} /> },
  { key: "plans", to: "/trainer/plans", label: "Schede", icon: <FileText size={16} /> },
  { key: "history", to: "/trainer/history", label: "Storico Lezioni", icon: <History size={16} /> },
  { key: "loads", to: "/trainer/loads", label: "Storico Schede", icon: <Dumbbell size={16} /> },
  { key: "stats", to: "/trainer/stats", label: "Statistiche", icon: <BarChart3 size={16} /> },
  { key: "messages", to: "/trainer/messages", label: "Messaggi", icon: <MessageCircle size={16} /> },
  { key: "reg", to: "/trainer/regulation", label: "Regolamento", icon: <BookOpen size={16} /> },
  { key: "settings", to: "/trainer/settings", label: "Impostazioni", icon: <Settings size={16} /> },
];

export default function TrainerDashboard() {
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    try {
      const [c, b] = await Promise.all([api.get("/clients"), api.get("/bookings")]);
      setClients(c.data); setBookings(b.data);
    } catch (e) { /* ignore transient errors from polling */ }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Overview clients={clients} bookings={bookings} />} />
        <Route path="calendar" element={<CalendarViewPage clients={clients} onChanged={load} />} />
        <Route path="clients" element={<ClientsPage clients={clients} onReload={load} />} />
        <Route path="plans" element={<TrainerPlansPage clients={clients} />} />
        <Route path="history" element={<LessonsHistoryPage role="admin" clients={clients} />} />
        <Route path="loads" element={<LoadHistoryPage role="admin" clients={clients} />} />
        <Route path="stats" element={<StatsPage />} />
        <Route path="messages" element={<MessagesAdminPage />} />
        <Route path="regulation" element={<RegulationPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/trainer" replace />} />
      </Route>
    </Routes>
  );
}

// Hoisted outside so its reference is stable across TrainerDashboard re-renders
// (auto-refresh setInterval was causing full remount → modal state loss).
function Shell() {
  const { pathname } = useLocation();
  const title = pathname.includes("calendar") ? "Calendario"
    : pathname.includes("clients") ? "Clienti"
    : pathname.includes("plans") ? "Schede fitness"
    : pathname.includes("history") ? "Storico Lezioni"
    : pathname.includes("loads") ? "Storico Schede"
    : pathname.includes("stats") ? "Statistiche"
    : pathname.includes("messages") ? "Messaggi clienti"
    : pathname.includes("regulation") ? "Regolamento"
    : pathname.includes("settings") ? "Impostazioni"
    : "Overview";
  return <AppShell nav={nav} title={title}><Outlet /></AppShell>;
}

function Overview({ clients, bookings }) {
  const today = new Date().toISOString().slice(0, 10);
  // Exclude cancelled bookings to match the calendar view.
  const todays = bookings
    .filter(b => b.date === today && b.status !== "cancelled")
    .sort((a, b) => a.time.localeCompare(b.time));
  const lowClients = clients.filter(c => (c.lessons_total - c.lessons_used) < 3);
  const expiringClients = clients.filter(c => (c.lessons_total - c.lessons_used) <= 2 && (c.status || "attivo") === "attivo");

  const addLessons = async (c) => {
    const n = window.prompt(`Quante lezioni aggiungere a ${c.display_name || c.name}?`, "10");
    if (!n) return;
    try {
      await api.post(`/clients/${c.id}/add-lessons?amount=${parseInt(n)}`);
      window.location.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <Stat title="Clienti attivi" value={clients.filter(c => (c.status || "attivo") === "attivo").length} className="lg:col-span-4" />
      <Stat title="Prenotazioni oggi" value={todays.length} className="lg:col-span-4" />
      <Stat title="Abbonamenti in scadenza" value={expiringClients.length} alert={expiringClients.length > 0} className="lg:col-span-4" />

      <div className="card-surface p-6 lg:col-span-7">
        <div className="label-caps mb-3">Oggi in studio</div>
        <div className="font-heading font-black uppercase tracking-tighter text-2xl mb-4">
          {today} · {todays.length} lezioni
        </div>
        {todays.length === 0 && <div className="text-white/40 text-sm">Nessuna lezione programmata oggi.</div>}
        <div className="space-y-2">
          {todays.map(b => (
            <div key={b.id} className="flex items-center justify-between border border-white/5 p-3">
              <div className="flex items-center gap-4">
                <div className="font-heading text-lg w-14">{b.time}</div>
                <div>
                  <div className="text-sm">{b.client_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{b.service_label}</div>
                </div>
              </div>
              <span className={`px-2 py-1 text-[10px] font-bold uppercase svc-${b.service_type}`}>{b.service_label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface p-6 lg:col-span-5">
        <div className="label-caps mb-3 flex items-center gap-2"><AlertTriangle size={12} className="text-[#FF3366]" /> Abbonamenti in scadenza</div>
        {expiringClients.length === 0 && <div className="text-white/40 text-sm">Tutti i clienti hanno abbonamenti sufficienti.</div>}
        <div className="space-y-2">
          {expiringClients.map(c => {
            const rem = c.lessons_total - c.lessons_used;
            const critical = rem <= 1;
            return (
              <div key={c.id} className="flex items-center justify-between border border-white/5 p-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.display_name || c.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{c.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-heading font-bold ${critical ? "text-[#FF3366] animate-pulse" : "text-[#F59E0B]"}`}>{rem}</span>
                  <button data-testid={`add-lessons-${c.id}`} onClick={() => addLessons(c)}
                    className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#A855F7] hover:bg-[#A855F7] transition-colors">
                    + Lezioni
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ title, value, alert, className = "" }) {
  return (
    <div className={`card-surface p-6 ${className} ${alert ? "border-[#FF3366]/40" : ""}`}>
      <div className="label-caps">{title}</div>
      <div className={`font-heading font-black text-5xl mt-2 ${alert ? "text-[#FF3366]" : ""}`}>{value}</div>
    </div>
  );
}

function CalendarViewPage({ clients, onChanged }) {
  return <CalendarView role="admin" clients={clients} onChanged={onChanged} />;
}
